import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import type {
  UISchemaQuestion,
  UISchemaSection,
} from 'projects/bylaws-kit/src/lib/ui-schema';
import {
  BylawsDraft,
  BylawsWizardService,
} from 'src/app/tools/bylaws/services/bylaws-wizard.service';

@Component({
  standalone: true,
  selector: 'bylaws-wizard-step',
  imports: [CommonModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './bylaws-wizard-step.component.html',
})
export class BylawsWizardStepComponent implements OnChanges {
  @Input() section!: UISchemaSection;
  @Input() form!: FormGroup;
  @Input() stepNumber = 1;
  @Input() totalSteps = 1;
  @Input() draft: Partial<BylawsDraft> | null = null;
  @Input() showErrors = false;

  /** Stable array used by *ngFor */
  displayItems: WizardDisplayItem[] = [];

  constructor(private readonly wizard: BylawsWizardService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section'] && this.section) {
      this.buildDisplayItems();
    }
  }

  private buildDisplayItems(): void {
    const items: WizardDisplayItem[] = [];
    const groupedIds = new Set<string>();

    for (const question of this.section.questions) {
      if (question.groupId) {
        if (groupedIds.has(question.groupId)) {
          continue;
        }
        groupedIds.add(question.groupId);
        const groupQuestions = this.section.questions.filter(
          (other) => other.groupId === question.groupId,
        );
        items.push({
          kind: 'group',
          groupId: question.groupId,
          label: question.groupLabel || question.label || '',
          questions: groupQuestions,
        });
      } else {
        items.push({ kind: 'question', question });
      }
    }

    this.displayItems = items;
  }

  onChange(question: UISchemaQuestion): void {
    const controlValue = this.form?.get(question.key)?.value;
    if (controlValue === undefined) {
      return;
    }
    this.wizard.update({ [question.key]: controlValue });
  }

  shouldShow(question: UISchemaQuestion): boolean {
    const dependency = question.visibleIf;
    if (!dependency) {
      return true;
    }

    if (dependency.endsWith('Specific')) {
      const field = dependency.replace(/Specific$/, '');
      const control = this.form?.get(field);
      const value =
        control?.value ?? (this.draft ? (this.draft as any)[field] : undefined);

      return value === 'Specific number';
    }

    const control = this.form?.get(dependency);
    const value =
      control?.value ??
      (this.draft ? (this.draft as any)[dependency] : undefined);
    return this.isTruthy(value);
  }

  /** New trackBy for the combined group/question list */
  trackItem(_index: number, item: WizardDisplayItem): string {
    return item.kind === 'group'
      ? `group-${item.groupId}`
      : `question-${item.question.key}`;
  }

  labelFor(question: UISchemaQuestion): string {
    let label = question.label || '';

    if (!this.draft) {
      return label;
    }

    const schoolName = this.draft.schoolName || 'the school';
    const orgName = this.draft.orgName || 'the organization';

    label = label.replace('[School Name]', schoolName);
    label = label.replace('[PTG Name]', orgName);

    return label;
  }

  helperText(question: UISchemaQuestion | null): string | undefined {
    if (!question) {
      return undefined;
    }
    return question.helperText ?? question.helpText;
  }

  /** Mirror the completion logic from isSectionComplete, but for a single question */
  isQuestionAnswered(question: UISchemaQuestion): boolean {
    const key = question.key;
    // Prefer value from draft (the normalized answers we actually use)
    const draftValue =
      this.draft && Object.prototype.hasOwnProperty.call(this.draft, key)
        ? (this.draft as any)[key]
        : undefined;

    const value =
      draftValue !== undefined ? draftValue : this.form?.get(key)?.value;

    if (question.type === 'boolean') {
      return value === true || value === false;
    }

    if (question.type === 'number') {
      return value !== null && value !== undefined && value !== '';
    }

    if (value === null || value === undefined) {
      return false;
    }

    return String(value).trim().length > 0;
  }

  private isTruthy(value: unknown): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === 'false' || normalized === 'no') {
        return false;
      }
      if (normalized === 'true' || normalized === 'yes') {
        return true;
      }
      return Boolean(normalized);
    }
    return Boolean(value);
  }
}

type WizardDisplayItem =
  | { kind: 'question'; question: UISchemaQuestion }
  | {
      kind: 'group';
      groupId: string;
      label: string;
      questions: UISchemaQuestion[];
    };
